import {
  DEFAULT_FAQ_SECTION_SETTINGS,
  type FaqSectionCategory,
  type FaqSectionItem,
  type FaqSectionPayload,
  type FaqSectionSettings,
  type FaqSectionTargetPage,
  type FaqSectionTemplate
} from './types';

export const normalizeFaqSectionTemplate = (value: unknown): FaqSectionTemplate =>
  value === 'tabs' ? 'tabs' : 'accordion';

export const getFaqSectionTemplateLabel = (template: FaqSectionTemplate): string =>
  template === 'tabs' ? 'Sekmeli (Template 2)' : 'Akordeon (Template 1)';

const cleanText = (value: unknown): string => String(value ?? '').trim();

const cleanCategories = (raw: unknown): FaqSectionCategory[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const id = cleanText(item?.id) || `cat_${index}`;
      const name = cleanText(item?.name);
      if (!name) return null;
      return {
        id,
        name,
        enabled: item?.enabled !== false,
        order: Number(item?.order) || index
      } satisfies FaqSectionCategory;
    })
    .filter(Boolean) as FaqSectionCategory[];
};

const cleanItems = (raw: unknown): FaqSectionItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const question = cleanText(item?.question);
      const answer = cleanText(item?.answer);
      if (!question || !answer) return null;
      return {
        id: cleanText(item?.id) || `faq_${index}`,
        question,
        answer,
        categoryId: cleanText(item?.categoryId) || undefined,
        enabled: item?.enabled !== false,
        order: Number(item?.order) || index
      } satisfies FaqSectionItem;
    })
    .filter(Boolean) as FaqSectionItem[];
};

const cleanTitleColor = (value: unknown): string => {
  const color = String(value ?? '').trim();
  if (!color) return '';
  // Metin rengi için solid hex tercih; gradient gelirse olduğu gibi saklanır
  return color;
};

const cleanSettings = (raw: unknown): FaqSectionSettings => {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    template: normalizeFaqSectionTemplate(src.template),
    colorMode: 'simple',
    titleColor: cleanTitleColor(src.titleColor)
  };
};

const FAQ_TAB_PREFIXES = [
  'about-tab:',
  'services-tab:',
  'product-solution-tab:',
  'reference-tab:',
  'custom-page:'
] as const;

export const isKnownFaqSectionTargetPage = (value: string): value is FaqSectionTargetPage =>
  value === 'homepage' ||
  value === 'about' ||
  value === 'services' ||
  value === 'products' ||
  value === 'products-solutions' ||
  value === 'references' ||
  FAQ_TAB_PREFIXES.some((prefix) => value.startsWith(prefix));

/** Alt sekme / sekme hedefleri (sayfa footer hedefleri hariç) */
export const isFaqSectionTabTarget = (value: string): boolean =>
  ['about-tab:', 'services-tab:', 'product-solution-tab:', 'reference-tab:'].some((prefix) =>
    value.startsWith(prefix)
  );

export const cleanFaqSectionPayload = (raw: Partial<FaqSectionPayload> | null | undefined): FaqSectionPayload => {
  const targetRaw = cleanText(raw?.position?.targetPage) || 'homepage';
  const targetPage = isKnownFaqSectionTargetPage(targetRaw) ? targetRaw : 'homepage';

  return {
    title: cleanText(raw?.title) || undefined,
    categories: cleanCategories(raw?.categories),
    items: cleanItems(raw?.items),
    settings: cleanSettings(raw?.settings || DEFAULT_FAQ_SECTION_SETTINGS),
    position: {
      targetPage,
      insertAfter: 'before-footer'
    }
  };
};
